/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { Dialog } from "../../../dialogs/Dialog";
import { AppPreviewComponent } from "./AppPreviewComponent";
import { TRootComponentListSchema } from "@adapptio/lib-components";
import { EditorRenderContext } from "../../../runtime/EditorRenderContext";
import { IEditor } from "../../../IEditor";
import { EditorRoutingManager } from "../../../runtime/EditorRoutingManager";
import { LayoutState } from "../../../ui/components/Layout/LayoutState";
import { AppPreviewLayoutConfig } from "./AppPreviewLayout";
import { AppPreviewHistory } from "./AppPreviewHistory";
import { BrowserLogger } from "../../../common/BrowserLogger";
import { MemoryStorage } from "../../../memoryStorage/MemoryStorage";
import { offEvent, onEvent } from "@adapptio/lib-shared";
import { RUNTIME_CONTEXT_MODE } from "@adapptio/lib-blueprint";

export interface AppPreview_Params {
	className?: string;
}

export interface AppPreview_Result {}

export class AppPreview extends Dialog<AppPreview_Params, AppPreview_Result> {
	private static STORAGE_KEY__LAST_PREVIEW_URL = Symbol("appPreview.lastPreviewUrl");

	private memoryStorage: MemoryStorage;

	/* Layout */
	private layoutState: LayoutState;

	/* Contexts */
	private renderCtx: EditorRenderContext<TRootComponentListSchema>;

	/* History */
	private history: AppPreviewHistory;

	/* Logger */
	private logger: BrowserLogger;

	private routingManager: EditorRoutingManager;

	constructor(dc: IEditor, id: string, params: AppPreview_Params) {
		super(dc, id, params);

		this.logger = dc.get("logger");

		this.memoryStorage = dc.get("memoryStorage");

		const defaultPath = this.memoryStorage.getOrSet(
			AppPreview.STORAGE_KEY__LAST_PREVIEW_URL,
			"/"
		) as string;

		this.routingManager = dc.get("runtimeService").createRoutingManager(defaultPath);

		this.history = new AppPreviewHistory({ routingManager: this.routingManager });

		this.renderCtx = dc.get("runtimeService").createRenderContext<TRootComponentListSchema>({
			mode: RUNTIME_CONTEXT_MODE.NORMAL,
			mediaResolution: "screenBase",
			routingManager: this.routingManager,
			getComponentListSpec: (spec) => spec,
			eventLogFn: this.logEvent,
			rootView: {
				viewName: "main",
				viewParams: {}
			}
		});

		this.renderCtx.getComponentListSpec();

		this.layoutState = new LayoutState(AppPreviewLayoutConfig);

		this.subscribeToHistoryChange();

		this.renderCtx.getRuntimeContext().render();
	}

	private subscribeToHistoryChange(): void {
		onEvent(this.history.onChange, this.storeCurrentPreviewUrl);
	}

	private unsubscribeFromHistoryChange(): void {
		offEvent(this.history.onChange, this.storeCurrentPreviewUrl);
	}

	public dispose = (): void => {
		this.unsubscribeFromHistoryChange();
		this.routingManager.dispose();
		this.history.dispose();

		this.layoutState.dispose();
		this.renderCtx.destroy();
	};

	private logEvent = (eventType, opts, metaData, result) => {
		if (window.APP_DEBUG) {
			// eslint-disable-next-line max-len
			this.logger.debug(
				"[EventSystem] Handled event '%s' #%d node ID '%s' (%s)",
				metaData.eventName,
				metaData.executionOrder,
				metaData.nodeId,
				eventType
			);
			this.logger.debug("Options:", opts);
			this.logger.debug("Result:", result);
			console.log("Sender:", {
				id: metaData.senderId,
				path: metaData.senderPath,
				modelNodeId: metaData.senderModelNodeId,
				instance: metaData.senderInstance
			});
			console.log("Input Scope:", metaData.inputScope);
			console.groupEnd();
		}
	};

	private storeCurrentPreviewUrl = (): void => {
		this.memoryStorage.set(AppPreview.STORAGE_KEY__LAST_PREVIEW_URL, this.history.current);
	};

	public getComponent(): typeof AppPreviewComponent {
		return AppPreviewComponent;
	}

	public getRenderContext(): EditorRenderContext<TRootComponentListSchema> {
		return this.renderCtx;
	}

	public getLayoutState(): LayoutState {
		return this.layoutState;
	}

	public getHistory = (): AppPreviewHistory => {
		return this.history;
	};

	public navigate(path: string): void {
		this.routingManager.navigate(path);
	}
}
